/**
 * Tracks a pending async operation with timeout awareness.
 *
 * Design principles:
 * - Knows when it was started
 * - Can determine if it's stale (exceeded timeout)
 * - Can be cancelled
 * - Immutable start time
 */
export class PendingRequest<T> {
  private readonly startedAt: number;
  private cancelled = false;

  constructor(
    public readonly promise: Promise<T>,
    public readonly timeoutMs: number
  ) {
    this.startedAt = Date.now();
  }

  /**
   * Check if request has exceeded its timeout.
   *
   * This is critical for detecting requests that started before
   * the browser was backgrounded and are now stale.
   */
  isStale(): boolean {
    return Date.now() - this.startedAt > this.timeoutMs;
  }

  /**
   * Get age of request in milliseconds.
   */
  getAge(): number {
    return Date.now() - this.startedAt;
  }

  /**
   * Mark request as cancelled.
   * The promise itself cannot be cancelled, but this flag
   * allows consumers to ignore the result.
   */
  cancel(): void {
    this.cancelled = true;
  }

  /**
   * Check if request was cancelled.
   */
  isCancelled(): boolean {
    return this.cancelled;
  }

  /**
   * Get remaining time before timeout (or 0 if already timed out).
   */
  getRemainingTime(): number {
    return Math.max(0, this.timeoutMs - this.getAge());
  }
}
